import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/AuthContext";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Servers } from "./pages/Servers";
import { Generator } from "./pages/Generator";
import { MessageLogs } from "./pages/MessageLogs";
import { Settings } from "./pages/Settings";
import { ServerLayout } from "./pages/ServerLayout";
import { StatusTab } from "./pages/server/StatusTab";
import { PlayersTab } from "./pages/server/PlayersTab";
import { ChatTab } from "./pages/server/ChatTab";
import { HelperTab } from "./pages/server/HelperTab";
import { ServerMessageLogsTab } from "./pages/server/ServerMessageLogsTab";

function FullPageMessage({ children }: { children: ReactNode }) {
  return (
    <div className="login-shell">
      <p className="muted">{children}</p>
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === "checking") {
    return <FullPageMessage>Checking session…</FullPageMessage>;
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }
  return <Layout>{children}</Layout>;
}

function LoginRoute() {
  const { status } = useAuth();
  if (status === "authenticated") {
    return <Navigate to="/" replace />;
  }
  return <Login />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />

          <Route path="/" element={<RequireAuth><Dashboard /></RequireAuth>} />
          <Route path="/servers" element={<RequireAuth><Servers /></RequireAuth>} />
          <Route path="/generator" element={<RequireAuth><Generator /></RequireAuth>} />
          <Route path="/message-logs" element={<RequireAuth><MessageLogs /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />

          <Route
            path="/servers/:id"
            element={
              <RequireAuth>
                <ServerLayout />
              </RequireAuth>
            }
          >
            <Route index element={<StatusTab />} />
            <Route path="players" element={<PlayersTab />} />
            <Route path="chat" element={<ChatTab />} />
            <Route path="helper" element={<HelperTab />} />
            <Route path="message-logs" element={<ServerMessageLogsTab />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
